import { useCallback, useEffect, useState } from 'react';
import { settingsApi } from '@/features/ai-dart/api/settings';
import { DartFormat } from '@/features/ai-dart/format';
import type { Orchestrator } from '@/features/ai-dart/types';
import { ActionButton, ActiveDot, TABLE, TD, TH, THEAD, TR } from '@/features/ai-dart/components/controls';
import { ConfirmModal, Modal } from '@/shared/ui/Modal';
import { Field, FieldRow, FormActions, FormError, Input, Select } from '@/features/ai-dart/components/form';

type Form = {
  id: number | null;
  name: string;
  kind: string;
  model: string;
  role: Orchestrator['role'];
  authKind: Orchestrator['authKind'];
  token: string;
};

const EMPTY: Form = { id: null, name: '', kind: 'claude', model: '', role: 'worker', authKind: 'oauth', token: '' };

const formOf = (row: Orchestrator): Form => ({
  id: row.id, name: row.name, kind: row.kind, model: row.model ?? '',
  role: row.role, authKind: row.authKind, token: '',
});

/**
 * 폼 → 서버 payload.
 *
 * `token`은 **비어 있으면 보내지 않는다**. 서버의 `StoredSecret.of`가 빈 값을 `none()`으로 접어 기존
 * 값을 유지하므로 보내도 사고는 아니지만, "안 건드림"을 요청 자체로 드러내는 편이 읽기 쉽다.
 * 반대로 `authKind`를 oauth로 **명시**하면 서버가 `tokenEnc=NULL`을 탄다 — 그건 의도된 폐기다.
 */
function payloadOf(form: Form): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    kind: form.kind.trim(),
    model: form.model.trim(),
    role: form.role,
    authKind: form.authKind,
  };
  if (form.authKind === 'api_key' && form.token.trim()) payload.token = form.token;
  return payload;
}

export function OrchestratorsView(): React.JSX.Element {
  const [rows, setRows] = useState<Orchestrator[]>([]); const [message, setMessage] = useState('');
  const load = useCallback(() => settingsApi.orchestrators.list().then(setRows).catch((error: Error) => setMessage(error.message)), []);
  useEffect(() => { void load(); }, [load]);

  const [form, setForm] = useState<Form | null>(null);   // null = 모달 닫힘
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<Orchestrator | null>(null);
  const [deactivating, setDeactivating] = useState<Orchestrator | null>(null);

  const set = (patch: Partial<Form>): void => setForm((current) => (current ? { ...current, ...patch } : current));

  /** 목록 조작(활성·삭제)의 결과는 섹션 머리에, 모달 안 조작의 결과는 모달에 띄운다. */
  const run = async (work: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try { await work(); setMessage('저장했습니다.'); load(); }
    catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  };
  const save = async (): Promise<void> => {
    if (!form) return;
    setBusy(true); setFormError('');
    try {
      const payload = payloadOf(form);
      await (form.id ? settingsApi.orchestrators.update(form.id, payload) : settingsApi.orchestrators.create(payload));
      setForm(null); setMessage('저장했습니다.'); load();
    } catch (error) { setFormError((error as Error).message); }
    finally { setBusy(false); }
  };

  /**
   * 활성 토글. 끄는 쪽만 확인을 받는다 — orchestrator·worker는 역할당 1개라, 켜져 있는 것을 끄면
   * 그 역할에 활성 모델이 없어져 파이프라인이 그 역할을 못 쓴다. reviewer는 패널이라 해당 없음.
   */
  const toggleActive = (row: Orchestrator): void => {
    if (row.isActive && row.role !== 'reviewer') { setDeactivating(row); return; }
    void run(() => settingsApi.orchestrators.activate(row.id));
  };

  return (
    <section className="rounded-lg border border-line bg-surface">
      <header className="flex items-center gap-3 border-b border-line p-4">
        <div>
          <h2 className="text-[13px] font-bold">모델</h2>
          <p className="text-[11px] text-faint">orchestrator·worker는 역할당 1개, reviewer는 패널로 여러 개 활성 가능합니다. 토큰은 마지막 4자만 표시합니다.</p>
        </div>
        <div className="ml-auto">
          <ActionButton tone="primary" onClick={() => { setFormError(''); setForm(EMPTY); }}>＋ 모델 등록</ActionButton>
        </div>
      </header>
      {message && <p className="border-b border-line bg-line-soft p-2 px-4 text-[12px]">{message}</p>}
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th className={TH}>활성</th>
              <th className={TH}>이름</th>
              <th className={TH}>종류</th>
              <th className={TH}>모델</th>
              <th className={TH}>역할</th>
              <th className={TH}>인증 · 토큰</th>
              <th className={TH}>갱신</th>
              <th className={`${TH} text-right`}>조작</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={TR}>
                <td className={TD}><ActiveDot on={row.isActive} label={row.isActive ? '활성' : '비활성'} /></td>
                <td className={`${TD} font-medium`}>{row.name}</td>
                <td className={`${TD} text-muted`}>{row.kind}</td>
                <td className={`${TD} font-mono`}>{row.model ?? <span className="font-sans text-faint">기본 모델</span>}</td>
                <td className={TD}><span className="rounded bg-line-soft px-2 py-0.5 text-[11px]">{row.role}</span></td>
                <td className={`${TD} text-muted`}>{row.authKind} · {row.tokenHint ?? (row.hasToken ? '저장됨' : '토큰 없음')}</td>
                <td className={`${TD} whitespace-nowrap text-faint`}>{DartFormat.kst(row.updatedAt)}</td>
                <td className={TD}>
                  <div className="flex justify-end gap-1.5">
                    <ActionButton tone={row.isActive ? 'default' : 'primary'} disabled={busy} onClick={() => toggleActive(row)}>
                      {row.isActive ? '비활성' : '활성'}
                    </ActionButton>
                    <ActionButton onClick={() => { setFormError(''); setForm(formOf(row)); }}>수정</ActionButton>
                    <ActionButton tone="danger" onClick={() => setRemoving(row)}>삭제</ActionButton>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="p-8 text-center text-muted" colSpan={8}>등록된 모델이 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={form !== null} onClose={() => setForm(null)} title={form?.id ? `모델 수정 · ${form.name}` : '모델 등록'}>
        {form && (
          <div>
            <Field label="이름 (고유)">
              <Input autoFocus value={form.name} onChange={(event) => set({ name: event.target.value })} placeholder="예: claude-opus-prod" />
            </Field>
            <Field
              label="역할"
              hint="orchestrator·worker는 역할당 1개만 활성입니다. reviewer는 패널이라 여러 개를 동시에 활성으로 둘 수 있습니다."
            >
              <Select value={form.role} onChange={(event) => set({ role: event.target.value as Form['role'] })}>
                <option value="orchestrator">orchestrator · 조율·스킬 실행(코드는 직접 쓰지 않음)</option>
                <option value="worker">worker · 코드 구현(오케가 위임)</option>
                <option value="reviewer">reviewer · 교차 검증(여러 개 가능)</option>
              </Select>
            </Field>
            <FieldRow>
              <Field label="종류">
                <Select value={form.kind} onChange={(event) => set({ kind: event.target.value })}>
                  {!['claude', 'codex'].includes(form.kind) && <option value={form.kind}>{form.kind} (현재)</option>}
                  <option value="claude">claude</option>
                  <option value="codex">codex</option>
                </Select>
              </Field>
              <Field label="모델 (비우면 기본 모델)">
                <Input value={form.model} onChange={(event) => set({ model: event.target.value })} placeholder="예: claude-opus-5" />
              </Field>
            </FieldRow>
            <Field
              label="인증 방식"
              hint={form.authKind === 'oauth' && form.id
                ? '⚠ api_key에서 oauth로 바꿔 저장하면 저장된 토큰이 삭제됩니다.'
                : undefined}
            >
              <Select value={form.authKind} onChange={(event) => set({ authKind: event.target.value as Form['authKind'] })}>
                <option value="oauth">oauth · 구독 로그인(토큰 불필요)</option>
                <option value="api_key">api_key · 종량(토큰 필요)</option>
              </Select>
            </Field>
            {form.authKind === 'api_key' && (
              <Field
                label="토큰"
                hint={form.id ? '봉투 암호화(AES-256-GCM)로 저장됩니다. 비워 두면 기존 토큰을 그대로 유지합니다.' : '봉투 암호화(AES-256-GCM)로 저장됩니다 — DB에는 암호문만 남습니다.'}
              >
                <Input
                  type="password"
                  autoComplete="off"
                  value={form.token}
                  onChange={(event) => set({ token: event.target.value })}
                  placeholder={form.id ? '변경할 때만 입력' : 'sk-ant-...'}
                />
              </Field>
            )}
            {formError && <FormError message={formError} />}
            <FormActions>
              <ActionButton tone="primary" disabled={busy || !form.name.trim()} onClick={() => void save()}>
                {form.id ? '저장' : '등록'}
              </ActionButton>
              <ActionButton onClick={() => setForm(null)}>취소</ActionButton>
            </FormActions>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={removing !== null}
        title="모델 삭제"
        confirmLabel="삭제"
        busy={busy}
        onClose={() => setRemoving(null)}
        onConfirm={() => { const row = removing; setRemoving(null); if (row) void run(() => settingsApi.orchestrators.remove(row.id)); }}
      >
        <strong>{removing?.name}</strong>({removing?.role})을 삭제합니다. 되돌릴 수 없습니다.
      </ConfirmModal>

      <ConfirmModal
        open={deactivating !== null}
        title="모델 비활성"
        confirmLabel="비활성"
        busy={busy}
        onClose={() => setDeactivating(null)}
        onConfirm={() => { const row = deactivating; setDeactivating(null); if (row) void run(() => settingsApi.orchestrators.activate(row.id)); }}
      >
        <strong>{deactivating?.name}</strong>을 비활성하면 <strong>{deactivating?.role}</strong> 역할에 활성 모델이 없어집니다.
        파이프라인이 그 역할을 쓰는 단계에서 멈춥니다.
      </ConfirmModal>
    </section>
  );
}
